// src/encounter/rail-add-popover.tsx
//
// The rail's add surface — one shell, every left-column card.
//
// WHY THIS EXISTS. Every `+` in the pre-encounter rail opened a CENTRED `Modal`: the flag
// picker, the medication form, allergies, problems, procedures, vaccinations, family and
// social history, occupation, education. The published app opens those from the rail as a
// surface ANCHORED to the `+` that was pressed, so the card you are adding to stays in
// view and stays the subject. A centred modal loses that — it dims the rail and re-centres
// attention on the screen, for a two-line pick.
//
// WHAT IT IS — and what it is NOT, since 2026-09-10. The CONTAINER is the Signal DS molecule
// `SummarySectionPopover` (Ahmed's ruling: the DS is the source of truth for this surface). It
// draws the 311px card, the 12px corner, `--shadow-overlay`, the 31px header band with its title
// and its close and confirm glyphs, the separator, and the clamped scrolling body. This file
// used to draw a hand-built copy of all of that, one pixel out on the header; that copy is gone.
//
// What is left here is the rail's CONTENT grammar inside the DS surface: a type-to-filter field,
// uppercase group labels, picker rows, and the empty/loading/no-match messages. Nothing here
// re-implements a DS component.
//
// The trigger side lives in `pre-encounter-parts.tsx`: `SummarySectionCard` accepts an
// `addPopover` render prop and hands the section's own `+` to `AnchoredPopoverTrigger` via
// `render`, which REPLACES the element rather than wrapping it — so the `+` keeps its measured
// Flutter geometry (12px glyph, 18px from the card's right edge, PROD-1549) and no button is
// nested inside another button.
//
// ANCHORING. `side="bottom" align="end"` puts the surface under the `+` and flush with the
// card's right edge, which is where the press was. The DS content portals to `<body>` by
// default, which is what escapes the rail's `overflow: auto` column — the same clipping that
// PROD's `SessionStatusControl` was cured of (DS 522ea20). The escape works because this app
// runs at root scale 1 (`--app-density-zoom: 1`, Signal DS `app.css`); the DS `container`
// escape hatch is for a zoomed root and is deliberately NOT used.
//
// STATE. The open state lives in `RailAnchoredSurface` and @base-ui's own dismissal is the only
// thing that closes it. The DS docblock is explicit that driving `open` from a host `onClick`
// toggle is measurably broken — dismissal fires `onOpenChange(false)` and a host handler flips
// it straight back to `true`, so a second press never closes and the exit transition never runs.
// Hosts that must close the surface after a successful pick get the `close` callback handed to
// the render prop. The header's own `X` is the DS molecule's `Popover.Close`, so it takes that
// identical path — which is why this file no longer threads a close callback of its own.
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnchoredPopoverItem, SummarySectionPopoverGroupLabel } from '@scripsteam/scrips-signal-ds';
import { Search } from 'lucide-react';
import { useRailFilterSlot, useRailSurfaceHeader, type RailSurfaceVariant } from './add-surface';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/** Left inset of every body row, of the filter, and of a form's fields: 16px (`--spacing-4`, the
 *  `px-4` below). The container's own 16px inset lives in the DS molecule; this is the CONTENT's,
 *  applied per row because a picker row is full-bleed (the DS item paints its hover across the
 *  whole surface) and carries the inset internally. That is also why the body is handed `px-0` —
 *  see `RailAddPopoverBody`. Row pitch is a 32px LINE BOX (`h-8`), not padding, so a two-word row
 *  cannot grow it. */

/** Mirrors the DS `ModalSize` union so a dialog can hand its existing `size` straight through
 *  when it renders anchored instead of centred. */
export type RailAddSurfaceSize = 'sm' | 'md' | 'lg';

/** How many pickable rows a surface may hold before it earns a filter field.
 *
 *  MEASURED, not chosen: Patient Flags reports `scrollHeight 974` inside a `clientHeight 320`
 *  body — thirty rows in one flat scroll, alphabetical, with no way to narrow them. Three
 *  screens of scrolling to reach "Work Restricitons" is a worse interaction than typing
 *  "work". Eight is the point at which the list stops fitting the body without scrolling
 *  (320px of body / the 32px row pitch, less a group label), so a surface that never scrolls
 *  never grows a control it does not need. */
export const RAIL_POPOVER_FILTER_THRESHOLD = 8;

export interface RailAddPopoverGroup {
  /** Uppercase group heading, e.g. the API's `categoryName` ("CHART", "COMMUNICATION").
   *  `null`/absent renders the rows with no heading rather than inventing one. */
  label?: string | null;
  items: RailAddPopoverItem[];
}

export interface RailAddPopoverItem {
  /** Stable within its group — used as the React key and returned by `onPick`. */
  key: string;
  label: string;
  disabled?: boolean;
  onSelect: () => void;
}

export interface RailAddPopoverProps {
  /** ACCEPTED AND IGNORED FOR WIDTH. It used to select one of three widths (364/480/640, the DS
   *  `Modal`'s own); the container now has a single measured width for every surface, so the
   *  prop no longer means anything geometric. It is kept because the ten rail dialogs still
   *  declare their `Modal` size and `AddSurface` hands it straight through — the SAME dialog
   *  rendered from the in-encounter banner is a centred `Modal` and its `size` is live there.
   *  Removing it would have meant editing ten dialogs to change nothing. */
  size?: RailAddSurfaceSize;
  /** The header band's label. DRAWN BY THE CONTAINER — published up through
   *  `useRailSurfaceHeader` and passed to the DS molecule's own `title` prop. The band renders
   *  either way (it carries the close and confirm glyphs), but with no `title` the DS renders no
   *  title element at all, because @base-ui wires it as the popup's `aria-labelledby` and an
   *  empty one would leave the surface with no accessible name — `RailAnchoredSurface` supplies
   *  an `aria-label` from the trigger in exactly that case. */
  title?: string;
  /** COMMIT the surface — what the header's checkmark does. Maps to the DS molecule's
   *  `onConfirm`, reached through a ref rather than published as state (a host passing an inline
   *  arrow hands a new identity every render; see `RailSurfaceHeader`).
   *
   *  Optional, and its absence is the DEFAULT state: the checkmark is drawn GREY, present but
   *  disabled. A picker that commits on a row tap (Patient Flags) supplies none. Every form-shaped
   *  rail dialog DOES supply one, through `AddSurface`: Ahmed's ruling (2026-10-07) retired their
   *  Cancel/Save footers — the header X cancels and this checkmark saves. */
  onCommit?: () => void;
  /** The checkmark's accessible name ("Save allergy"). Maps to the DS molecule's `confirmLabel`;
   *  absent → the DS default "Confirm". */
  confirmLabel?: string;
  /** Surface width in px, mapped to the DS molecule's `width`. Absent → the DS default 311. Only
   *  Medications passes one (400, Flutter's width for that form). */
  width?: number;
  /** Which DS chrome draws the surface (`SummarySectionPopover` `variant`). Absent → `rail`. The
   *  Flutter production add/detail popups (Medication Details) pass `detail`: 36px band, 20px
   *  glyphs, brand-depth title, the DS's own 336 width. */
  variant?: RailSurfaceVariant;
  /** Whether the header draws the checkmark at all (DS `showConfirm`). Absent → drawn. A picker
   *  that commits on a row tap passes false (Patient Flags, Ahmed's ruling 2026-10-07): a
   *  permanently grey checkmark there is noise. */
  showConfirm?: boolean;
  groups?: RailAddPopoverGroup[];
  loading?: boolean;
  /** Blocks every row while a mutation is in flight, without unmounting the surface. */
  saving?: boolean;
  /** Shown when there is nothing to pick and nothing is loading. */
  emptyLabel?: string;
  /** Arbitrary content instead of groups — the form-shaped add flows use this. */
  children?: ReactNode;
  /**
   * Whether the body must RESERVE its height (DS `bodyHeight="reserved"`), stated by the host
   * that knows its own form. Absent → decided here: a picker reserves (its rows, a filter or a
   * load can still arrive), and a form reserves only while its search shows results or
   * "Searching…" (a `role="listbox"` / `role="status"` in the form); otherwise a form hugs.
   * Before 2026-10-07 any type-in field reserved from open, which left short forms blank.
   */
  reserve?: boolean;
}

/** A one-line message (empty / no match): 13px on a 16px line at the 16px inset, 6px above and
 *  below. 13px is the rail's own body size (hand-built anatomy, Andrew 2026-08-08). */
const MESSAGE = 'py-1.5 px-4 text-size-sm leading-4 text-text-secondary';

/** A body row: a 32px LINE BOX at the 16px inset, replacing the DS item's `px-3 py-1.5` and its
 *  `block` — `AnchoredPopoverItem` merges its classes with `tailwind-merge`, so these win their
 *  class groups outright (display, height, padding) and the text sits optically centred. Colour
 *  and hover stay the DS's (its colour classes are untouched), so this is geometry only. */
const ROW = 'flex items-center h-8 py-0 px-4';

/** The filter field, on a long picker only (see `RAIL_POPOVER_FILTER_THRESHOLD`).
 *
 *  DELIBERATELY CHROMELESS — no border, no box, no focus ring of its own. The container is a
 *  311px surface whose whole visual weight is one shadowed plane; a bordered 40px DS `TextField`
 *  inside it would out-shout the rows it exists to serve. The search glyph beside it is what
 *  says "type here", and it is the same 13px/16px idiom as the body. */
const FILTER_INPUT =
  'flex-1 min-w-0 h-5 p-0 border-0 outline-none bg-transparent font-family-inherit ' +
  'text-size-sm leading-4 text-text-primary';

/** What a form's typeahead draws WHILE its results are arriving or showing: the DS `CodeSearchField`
 *  renders a `role="status"` line ("Searching…", "No matching codes") and an inline
 *  `role="listbox"` of results. Both vanish once a code is picked (the field becomes a chip).
 *  A `Select`'s listbox is portalled to `<body>`, so it never matches inside the form. */
const ARRIVING_RESULTS = "[role='listbox'], [role='status']";

/**
 * The CONTENT of a rail add surface — everything inside the Signal DS
 * `SummarySectionPopover`, and nothing of the container itself.
 *
 * The container is the DS molecule (Ahmed's ruling, 2026-09-10: it is the source of truth for
 * this surface). `RailAnchoredSurface` in `pre-encounter-parts.tsx` renders it; this renders
 * its `children`. What was deleted from here — the 32px header band, its close and confirm
 * glyphs, the separator, the 311px width, the 12px corner, the shadow, the scroll body and its
 * clamp — was a hand-built lookalike of that molecule, one pixel out on the header (32 against
 * the DS's 31, because the PA counted the separator inside the band as well as after it).
 *
 * WHAT IS STILL HERE, and why the DS does not provide it:
 *   · the type-to-filter field, on a picker long enough to need one;
 *   · the group labels, picker rows, empty/loading/no-match messages;
 *   · the 16px inset a form body needs, which a full-bleed picker row must not get;
 *   · the height RESERVATION signal, so the box cannot resize under the user mid-keystroke.
 * Each is recorded as a DS follow-up in `_session-cache/popover-ds-swap-2026-09-10/RESULT.md`.
 *
 * The title and the commit action are declared here but DRAWN by the container, so they travel
 * up through `useRailSurfaceHeader` — see `add-surface.tsx` for why that is a value publish and
 * not a callback publish.
 */
export function RailAddPopoverBody({
  title,
  groups,
  loading,
  saving,
  onCommit,
  confirmLabel,
  width,
  variant,
  showConfirm,
  emptyLabel = 'Nothing to add.',
  children,
  reserve,
}: RailAddPopoverProps) {
  const hasGroups = (groups?.length ?? 0) > 0;

  // TYPE TO NARROW — only on a surface long enough to need it. See
  // `RAIL_POPOVER_FILTER_THRESHOLD` for why eight, and for the measurement (thirty flags,
  // `scrollHeight 974` in a 320px body) that says this is a real interaction and not a
  // decoration. Matching is a plain case-insensitive substring on the visible label: the rows
  // are short clinical phrases, so fuzzy matching would only invent surprises.
  const [query, setQuery] = useState('');
  const total = useMemo(
    () => groups?.reduce((n, g) => n + g.items.length, 0) ?? 0,
    [groups],
  );
  const filterable = total > RAIL_POPOVER_FILTER_THRESHOLD;
  const needle = filterable ? query.trim().toLowerCase() : '';
  const shownGroups = useMemo(() => {
    if (!needle) return groups;
    return groups
      ?.map((g) => ({ ...g, items: g.items.filter((i) => i.label.toLowerCase().includes(needle)) }))
      // A group whose every row was filtered out must lose its heading too, or the surface
      // shows "COMMUNICATION" over nothing.
      .filter((g) => g.items.length > 0);
  }, [groups, needle]);
  const filteredToNothing = needle !== '' && (shownGroups?.length ?? 0) === 0;

  // RESERVE THE BODY'S HEIGHT so the box cannot resize under the user while they type.
  //
  // THE DEFECT, measured across all sixteen rail popovers at 1920x911 (Playwright, so nothing is
  // throttled — the Chrome extension's tab kept backgrounding and produced three wrong readings
  // before this was believed):
  //
  //   Past Surgical History   top   top 482 -> 439 (-43)   height 323 -> 366 (+43)
  //   Vaccinations            top   top 367 -> 313 (-54)   height 323 -> 377 (+54)
  //
  // `dTop === -dHeight` exactly, in both, and that identity IS the mechanism. Six of the eight
  // section surfaces open `side: top` (a 377px box does not fit below an anchor that low in a
  // 911px viewport, so `collisionAvoidance={{ side: 'flip' }}` flips it), and @base-ui pins a
  // top-side popup's BOTTOM to the anchor. The box therefore grows UPWARD. The height hugged its
  // content, so when a search returned rows the body grew 266 -> 320 and the whole surface slid up
  // by exactly that much — under the caret, while the user was typing, and with the results moving
  // out from under the pointer that was about to click one.
  //
  // WHY IT LOOKED INCONSISTENT (Ahmed's actual report — "any popover … moves to top"): the four
  // stable surfaces are stable only by accident. Allergies, Active Problems, Family History and
  // Social History happen to open ALREADY at the 320px cap, so they physically cannot grow. With a
  // patient carrying less data they would jump too.
  //
  // THE CURE is to stop the height depending on how much content happens to have loaded. It also
  // breaks a feedback loop: the height is derived from `--available-height`, which the positioner
  // rewrites on every reposition, so height and position were feeding each other.
  //
  // RESERVED ONLY WHERE CONTENT CAN ARRIVE LATER, which is the whole of Ahmed's ruling ("make them
  // they have the same stable expereience") without inflating a surface that has nothing to wait
  // for. A picker's rows are fetched by the open itself (`onAddOpenChange` enables the query), so
  // `groups`/`loading`/a filter all mean "more may arrive". A FORM reserves when it holds a field
  // A FORM reserves only WHILE its search is showing results (or "Searching…") — the medication
  // form's rule (`reserve={searching}`), read here for every form from its own DOM.
  //
  // CHANGED 2026-10-07 (PROD-3086, owner: "fix the height blank area for sections that don't have a
  // lot of content"). A form used to reserve from OPEN whenever it held any type-in field, so Add
  // procedure, Add vaccine, Add allergy, Add occupation… all opened at the full 320px clamp with
  // ~120px of empty white under a three-field form. Now a form hugs; the box takes the clamp
  // the moment results start to arrive and keeps it while they show, so results then change
  // under the caret without moving the box, and it hugs again once a code is picked.
  // A host that knows better still passes `reserve`.
  const formRef = useRef<HTMLDivElement>(null);
  const [formShowsResults, setFormShowsResults] = useState(false);
  // `?x=rail-fixes` (Samer, 3 Nov Loom: "it's showing only two"): stamps the form so the CSS lets its body take the available height
  // while results show (showcase-bridge.css). Read once per mount.
  const [railFixes] = useState(() => isEncounterFlagOn('railFixes'));
  // No dependency array on purpose: results arrive as props of the DIALOG, which re-renders this
  // body; the check is a single scoped `querySelector` that only sets state on change.
  useLayoutEffect(() => {
    const next = formRef.current?.querySelector(ARRIVING_RESULTS) != null;
    setFormShowsResults((prev) => (prev === next ? prev : next));
  });
  const reserveBodyHeight = reserve ?? (filterable || loading === true || hasGroups || formShowsResults);

  // FOCUS THE FILTER WHEN IT ARRIVES, not only when the surface opens.
  //
  // @base-ui evaluates `initialFocus` once, on open — and every picker here loads its rows from
  // a query that is only enabled BY the open (`onAddOpenChange`), so at that moment the body is
  // empty and there is nothing to focus. Measured: `document.activeElement` was the popup shell
  // (and, from a scripted open, `<body>`), so the list arrived under a caret that was nowhere.
  //
  // Guarded so it can only ever CLAIM focus, never steal it: it runs when the field first
  // mounts, and only while focus is still parked somewhere that means "nobody has chosen a
  // target yet". A user who has already tabbed or clicked into the surface keeps their place.
  //
  // THE TRIGGER COUNTS AS PARKED, and that is the case that matters in a real browser.
  // MEASURED in Chrome: after a MOUSE press on the `+`, `document.activeElement` is the
  // trigger button itself — @base-ui pulls focus into a non-modal popover only for KEYBOARD
  // activation, so `initialFocus` never runs on the common path. The first guard therefore
  // declined every time and the caret sat on the `+`, where typing does nothing. Moving it
  // into the surface the user just opened from that same button is not theft; it is the
  // interaction they asked for. (jsdom does focus the popup, which is why the suite could not
  // see this — the browser measurement is the evidence here, not the test.)
  const filterRef = useRef<HTMLInputElement>(null);
  const { hostsFilter, filterSlot } = useRailFilterSlot();
  useEffect(() => {
    const el = filterRef.current;
    if (!el) return;
    const active = document.activeElement;
    const parked =
      !active ||
      active === document.body ||
      active.getAttribute('data-slot') === 'anchored-popover-content' ||
      // Only one rail popover can be open at a time, so any open trigger is this one's.
      (active.getAttribute('data-slot') === 'anchored-popover-trigger' &&
        active.getAttribute('aria-expanded') === 'true');
    if (parked) el.focus();
    // `filterSlot`: the field mounts for the first time when the DS band's slot arrives.
  }, [filterable, filterSlot]);
  // Publish what the CONTAINER draws. The DS `SummarySectionPopover` takes the title and the
  // commit action as props, and both are declared here — so they travel up. See
  // `useRailSurfaceHeader` for why the callback goes by ref and the rest by value.
  useRailSurfaceHeader(
    { title, hasCommit: !!onCommit, reserve: reserveBodyHeight, filterable, confirmLabel, width, variant, showConfirm },
    onCommit,
  );

  // THE FILTER, on a long picker only (see `RAIL_POPOVER_FILTER_THRESHOLD`). Its state is HERE,
  // but it is DRAWN in the DS molecule's `filter` band — between the separator and the scrolling
  // body, outside the scroller, so it holds while the rows scroll without a sticky pin. Only the
  // boolean `filterable` travels up; the field is portalled into the band's slot, so a keystroke
  // re-renders this body and never the container (publishing the field itself as state is the
  // render loop `RailSurfaceHeader` documents). The band is `flex items-center gap-2 px-4` at a
  // fixed 32px, which is exactly the row this field used to draw for itself.
  const filterField = filterable ? (
    <>
      <Search className="size-4 shrink-0 text-text-hint" aria-hidden />
      <input
        ref={filterRef}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Filter the list"
        placeholder="Type to filter…"
        className={FILTER_INPUT}
      />
    </>
  ) : null;

  // A FRAGMENT, deliberately: these become DIRECT children of the DS body, which is what §22's
  // settle rule (`[data-slot='summary-section-popover-body'] > *`) animates and what §23's
  // `:has()` reservation and mask rules look for. Wrapping them in one div would animate the
  // wrapper alone and hide the filter from those selectors.
  return (
    <Fragment>
      {/* A container with the DS band gets the field there (once its slot has mounted); one
          without it keeps the field inline as the body's first row, on the same geometry. */}
      {filterField &&
        (hostsFilter ? (
          filterSlot && createPortal(filterField, filterSlot)
        ) : (
          <div data-slot="rail-popover-filter" className="flex items-center gap-2 py-1.5 px-4">
            {filterField}
          </div>
        ))}
      {loading && <QuietLoading label="Loading…" />}
      {!loading && !hasGroups && !children && <p className={MESSAGE}>{emptyLabel}</p>}
      {/* Distinct from `emptyLabel`, which means "this section has nothing to add". A filter
          that matched nothing is a different fact and quoting the query back is what tells
          the user it was their typing and not an empty list. */}
      {!loading && filteredToNothing && (
        <p className={MESSAGE}>No matches for “{query.trim()}”.</p>
      )}
      {/* The 16px inset belongs to the CONTENT, not to the DS body — which is handed `px-0`
          precisely because a picker ROW is full-bleed by design (the DS item paints its own
          hover across the whole surface) and carries the same inset internally, so a shared
          parent padding would double it to 32. Without this a form sat flush at x=0 against a
          12px-radius corner, so a full-width field's border ran under the rounded edge and its
          focus ring was clipped by `overflow`. */}
      {children != null && (
        <div
          ref={formRef}
          data-slot="rail-popover-form"
          data-rail-fixes={railFixes ? 'on' : undefined}
          data-shows-results={formShowsResults ? 'true' : undefined}
          className="px-4"
        >
          {children}
        </div>
      )}
      {!loading &&
        shownGroups?.map((g, i) => (
          // Index is part of the key on purpose: `categoryName` comes off the wire and two
          // groups sharing one would otherwise collide.
          <section key={(g.label ?? 'ungrouped') + '-' + i}>
            {/* The DS group heading: pinned while its rows scroll (sticky, on the card
                ground), in the eyebrow idiom this row's inline style used to draw. */}
            {g.label && <SummarySectionPopoverGroupLabel>{g.label}</SummarySectionPopoverGroupLabel>}
            {g.items.map((item) => (
              <AnchoredPopoverItem
                key={item.key}
                disabled={saving || item.disabled}
                onClick={item.onSelect}
                className={ROW}
              >
                {item.label}
              </AnchoredPopoverItem>
            ))}
          </section>
        ))}
    </Fragment>
  );
}
